import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { apiService } from '../services/api';
import { UserRole } from '../types';
import { useUser } from '../contexts/UserContext';

const UserProfile: React.FC = () => {
  const { user, loading, updateUser } = useUser();
  const { logout } = useAuth0();
  const [switchingRole, setSwitchingRole] = useState(false);

  const handleRoleSwitch = async () => {
    if (!user || switchingRole) return;

    setSwitchingRole(true);
    try {
      const newRole = user.role.name === UserRole.EDITOR ? UserRole.VIEWER : UserRole.EDITOR;
      const updatedUser = await apiService.updateCurrentUserRole(newRole);
      
      // Update the user in context, which will propagate to all components
      updateUser(updatedUser);
    } catch (error) {
      console.error('Error switching role:', error);
    } finally {
      setSwitchingRole(false);
    }
  };

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3">
        <div className="bg-gray-100 rounded-full w-9 h-9 flex items-center justify-center shadow-md border-2 border-white animate-pulse">
          <div className="text-xs text-gray-400">...</div>
        </div>
        <div className="flex-1">
          <div className="font-medium text-sm text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const initials = user.name
    .split(' ')
    .map(name => name.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const getTargetRoleName = () => {
    return user.role.name === UserRole.EDITOR ? 'Viewer' : 'Editor';
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* User Info */}
      <div className="flex items-center gap-3">
        {user.picture ? (
          <img 
            src={user.picture} 
            alt={user.name}
            className="rounded-full w-9 h-9 object-cover shadow-sm border border-gray-200"
          />
        ) : (
          <div className="bg-gray-900 text-white rounded-full w-9 h-9 flex items-center justify-center font-bold text-sm shadow-sm border border-gray-200">
            {initials}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-sm text-gray-900 truncate">{user.name}</span>
            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full font-medium">
              {user.role.name.toLowerCase()}
            </span>
          </div>
          <div className="text-xs text-gray-500 truncate">
            {user.email}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-2">
        <button
          onClick={handleRoleSwitch}
          disabled={switchingRole}
          className={`
            w-full px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 border
            ${switchingRole 
              ? 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200' 
              : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200 hover:border-gray-300'
            }
          `}
        >
          {switchingRole ? 'Switching...' : `Change to ${getTargetRoleName()}`}
        </button>
        
        <button
          onClick={handleLogout}
          className="w-full px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 border bg-red-50 text-red-700 hover:bg-red-100 border-red-200 hover:border-red-300"
        >
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default UserProfile; 